import { BodyCopy } from "../components/BodyCopy";
import { PageTitle } from "lola-framework-ui-test";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useStore } from "../store/globalState";
import { countryDisplayName } from "../utils/country-display";

export type RouteUnavailableReason = "amount" | "destination";

export interface RouteUnavailableInfoProps {
  /** Destination country ISO alpha-2 (e.g. `MX`). Used when reason is destination. */
  destinationCountry: string;
  /**
   * Why the route can't proceed. `amount` = no quotable funding method for this
   * send amount (over max, under min, or a gap between rails). `destination` =
   * corridor itself.
   */
  reason?: RouteUnavailableReason;
  /**
   * Catalog funding method (e.g. `DEBIT_CARD`) when the user / link already
   * pinned one — used to name the payment method in the amount-limit copy.
   */
  fundingMethod?: string | null;
}

export const RouteUnavailableInfo: React.FC<RouteUnavailableInfoProps> = ({
  destinationCountry,
  reason = "amount",
  fundingMethod,
}) => {
  const { t, i18n } = useTranslation();
  const { theme } = useStore();
  const colorConfig = theme.errorView;
  const { title, bodyCopy } = colorConfig;

  const countryName = useMemo(
    () => countryDisplayName(destinationCountry, i18n.language || "en"),
    [destinationCountry, i18n.language]
  );

  const methodLabel = fundingMethod
    ? t(`funding.methods.${fundingMethod}`, { defaultValue: fundingMethod })
    : null;

  const heading =
    reason === "destination"
      ? t("routeUnavailable.errorTitle")
      : t("routeUnavailable.amountLimitTitle");

  const message =
    reason === "destination"
      ? t("routeUnavailable.message", { destination: countryName })
      : methodLabel
        ? t("routeUnavailable.amountLimitMessageWithMethod", {
            method: methodLabel,
          })
        : t("routeUnavailable.amountLimitMessage");

  const hint =
    reason === "destination"
      ? t("routeUnavailable.hint")
      : t("routeUnavailable.amountLimitHint");

  return (
    <section className="landing-container">
      <PageTitle
        highlight={heading}
        highlightColor={title}
        secudnary=""
        secudnaryColor={title}
      />
      <BodyCopy textColor={bodyCopy}>{message}</BodyCopy>
      <BodyCopy textColor={bodyCopy}>{hint}</BodyCopy>
    </section>
  );
};
